import Image from "next/image"
import Link from "next/link"
import { ArrowRight, CheckCircle2, MapPin, Play, Sparkles, Star } from "lucide-react"

import { MotionDiv, MotionList, MotionSection, blurIn, fadeInLeft, fadeInRight, scaleIn } from "@/components/motion"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import {
  LocalAuthorityMagneticCta,
  LocalAuthorityMediaTilt,
  LocalAuthorityPulse,
  LocalAuthorityTrustLift,
} from "./local-authority-motion"
import { LiftCard, MagneticCta, TiltCard } from "@/components/motion-primitives"

export type HeroCta = {
  label: string
  href: string
}

export type HeroMetric = {
  value: string
  label: string
}

export type HeroMedia = {
  src: string
  alt: string
}

export type ProductShowcaseHeroProps = {
  eyebrow?: string
  title: string
  description: string
  primaryCta?: HeroCta
  secondaryCta?: HeroCta
  media?: HeroMedia
  metrics?: HeroMetric[]
  featureBullets?: string[]
  className?: string
}

/** Product-forward hero for SaaS, platforms, apps, and launches with a polished visual artifact. */
export function ProductShowcaseHero({
  eyebrow,
  title,
  description,
  primaryCta,
  secondaryCta,
  media,
  metrics = [],
  featureBullets = [],
  className,
}: ProductShowcaseHeroProps) {
  return (
    <MotionSection className={cn("relative isolate overflow-hidden bg-foreground py-20 text-background sm:py-24 lg:py-28", className)}>
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_18%_18%,hsl(var(--primary)/0.24),transparent_30%),radial-gradient(circle_at_82%_28%,hsl(var(--accent)/0.22),transparent_28%),linear-gradient(135deg,hsl(var(--foreground)),hsl(var(--foreground)/0.92))]" />
      <div className="mx-auto grid w-full max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
        <MotionDiv variants={fadeInLeft} className="max-w-3xl">
          {eyebrow && (
            <Badge className="mb-6 rounded-full border-background/14 bg-background/10 px-4 py-2 text-background backdrop-blur-xl">
              <Sparkles className="mr-2 size-3.5 text-accent" />
              {eyebrow}
            </Badge>
          )}
          <h1 className="font-heading text-5xl font-semibold leading-[0.9] tracking-tight sm:text-6xl lg:text-7xl">
            {title}
          </h1>
          <p className="mt-7 max-w-2xl text-lg leading-8 text-background/72 sm:text-xl">
            {description}
          </p>

          {(primaryCta || secondaryCta) && (
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              {primaryCta && (
                <Button asChild size="xl" className="group rounded-button bg-background text-foreground hover:bg-background/90">
                  <Link href={primaryCta.href}>
                    {primaryCta.label}
                    <ArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </Button>
              )}
              {secondaryCta && (
                <Button asChild size="xl" variant="outline" className="rounded-button border-background/22 bg-background/10 text-background backdrop-blur-xl hover:bg-background/16">
                  <Link href={secondaryCta.href}>
                    <Play className="size-4" />
                    {secondaryCta.label}
                  </Link>
                </Button>
              )}
            </div>
          )}

          {featureBullets.length > 0 && (
            <MotionList className="mt-9 grid gap-3 sm:grid-cols-2">
              {featureBullets.map((bullet) => (
                <MotionDiv key={bullet} variants={scaleIn}>
                  <LiftCard>
                    <div className="flex h-full items-center gap-3 rounded-card border border-background/12 bg-background/10 px-4 py-3 text-sm text-background/78 backdrop-blur-xl">
                      <CheckCircle2 className="size-4 shrink-0 text-accent" />
                      {bullet}
                    </div>
                  </LiftCard>
                </MotionDiv>
              ))}
            </MotionList>
          )}
        </MotionDiv>

        <MotionDiv variants={fadeInRight} className="relative">
          <div className="absolute -inset-10 -z-10 rounded-full bg-primary/20 blur-[90px]" />
          <TiltCard intensity={4}>
          <Card className="overflow-hidden rounded-panel border-background/14 bg-background/10 p-3 text-background shadow-[0_34px_110px_hsl(var(--foreground)/0.38)] backdrop-blur-2xl">
            <div className="rounded-card border border-background/10 bg-foreground/80 p-3">
              <div className="mb-3 flex items-center justify-between rounded-card border border-background/10 bg-background/8 px-4 py-3 text-xs text-background/58">
                <span>Live workspace</span>
                <span className="rounded-full bg-accent px-3 py-1 text-accent-foreground">Active</span>
              </div>
              <div className="relative min-h-[24rem] overflow-hidden rounded-card bg-muted lg:min-h-[34rem]">
                {media ? (
                  <Image src={media.src} alt={media.alt} fill sizes="(min-width: 1024px) 52vw, 100vw" className="object-cover" priority />
                ) : (
                  <div className="absolute inset-0 bg-[linear-gradient(135deg,hsl(var(--background)/0.95),hsl(var(--muted))),radial-gradient(circle_at_80%_20%,hsl(var(--primary)/0.18),transparent_30%)]" />
                )}
                {metrics.length > 0 && (
                  <div className="absolute inset-x-4 bottom-4 grid gap-3 rounded-panel border border-background/16 bg-foreground/74 p-3 backdrop-blur-xl sm:grid-cols-3">
                    {metrics.map((metric) => (
                      <div key={metric.label} className="rounded-card bg-background/10 p-4">
                        <p className="font-heading text-2xl font-semibold tracking-tight text-background">{metric.value}</p>
                        <p className="mt-1 text-xs uppercase tracking-[0.16em] text-background/56">{metric.label}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Card>
          </TiltCard>
        </MotionDiv>
      </div>
    </MotionSection>
  )
}

export type LocalAuthorityHeroProps = {
  eyebrow?: string
  title: string
  description: string
  location?: string
  primaryCta?: HeroCta
  secondaryCta?: HeroCta
  media?: HeroMedia
  trustPoints?: string[]
  rating?: string
  className?: string
}

/** Location and service hero for venues, clinics, hospitality, home services, and premium local brands. */
export function LocalAuthorityHero({
  eyebrow,
  title,
  description,
  location,
  primaryCta,
  secondaryCta,
  media,
  trustPoints = [],
  rating,
  className,
}: LocalAuthorityHeroProps) {
  return (
    <section className={cn("relative isolate overflow-hidden bg-background py-20 sm:py-24 lg:py-28", className)}>
      <div className="absolute inset-0 -z-10 bg-[linear-gradient(180deg,hsl(var(--background)),hsl(var(--muted)/0.58))]" />
      <div className="mx-auto grid w-full max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:px-8">
        <MotionDiv variants={fadeInLeft} className="max-w-3xl">
          <div className="mb-6 flex flex-wrap items-center gap-3">
            {eyebrow && <Badge variant="secondary" className="rounded-full px-4 py-2 text-primary">{eyebrow}</Badge>}
            {location && (
              <span className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-card px-4 py-2 text-sm text-muted-foreground shadow-sm">
                <MapPin className="size-4 text-primary" />
                {location}
              </span>
            )}
          </div>
          <h1 className="font-heading text-5xl font-semibold leading-[0.92] tracking-tight text-foreground sm:text-6xl lg:text-7xl">
            {title}
          </h1>
          <p className="mt-7 max-w-2xl text-lg leading-8 text-muted-foreground sm:text-xl">
            {description}
          </p>

          {(primaryCta || secondaryCta) && (
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              {primaryCta && (
                <Button asChild size="xl" className="group rounded-button">
                  <Link href={primaryCta.href}>
                    {primaryCta.label}
                    <ArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </Button>
              )}
              {secondaryCta && (
                <Button asChild size="xl" variant="outline" className="rounded-button border-border/70 bg-background/80">
                  <Link href={secondaryCta.href}>{secondaryCta.label}</Link>
                </Button>
              )}
            </div>
          )}

          {trustPoints.length > 0 && (
            <MotionList className="mt-8 flex flex-wrap gap-2">
              {trustPoints.map((point) => (
                <MotionDiv key={point} variants={scaleIn}>
                  <LocalAuthorityTrustLift>
                    <div className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-card px-3.5 py-2 shadow-sm">
                      <CheckCircle2 className="size-4 shrink-0 text-primary" />
                      <p className="whitespace-nowrap text-sm font-medium text-foreground">{point}</p>
                    </div>
                  </LocalAuthorityTrustLift>
                </MotionDiv>
              ))}
            </MotionList>
          )}
        </MotionDiv>

        <MotionDiv variants={fadeInRight} className="relative">
          <div className="absolute inset-x-8 bottom-0 top-10 -z-10 rounded-panel bg-primary/12 blur-3xl" />
          <LocalAuthorityMediaTilt>
            <div className="relative min-h-[34rem] overflow-hidden rounded-panel border border-border/70 bg-card shadow-[0_28px_90px_hsl(var(--foreground)/0.16)]">
              {media ? (
                <Image src={media.src} alt={media.alt} fill sizes="(min-width: 1024px) 45vw, 100vw" className="object-cover" priority />
              ) : (
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_18%,hsl(var(--accent)/0.38),transparent_34%),linear-gradient(135deg,hsl(var(--muted)),hsl(var(--background)))]" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/72 via-foreground/12 to-transparent" />
              {rating && (
                <div className="absolute bottom-5 left-5 right-5 rounded-panel border border-background/14 bg-background/92 p-5 text-foreground shadow-elegant backdrop-blur-xl" style={{ transform: "translateZ(40px)" }}>
                  <div className="mb-3 flex gap-1 text-accent">
                    {Array.from({ length: 5 }).map((_, index) => <Star key={index} className="size-4 fill-current" />)}
                  </div>
                  <p className="font-heading text-2xl font-semibold tracking-tight">{rating}</p>
                </div>
              )}
            </div>
          </LocalAuthorityMediaTilt>
        </MotionDiv>
      </div>
    </section>
  )
}

export type ProofLedSplitHeroProps = {
  eyebrow?: string
  title: string
  description: string
  primaryCta?: HeroCta
  secondaryCta?: HeroCta
  quote?: {
    body: string
    name: string
    role?: string
  }
  stats?: HeroMetric[]
  className?: string
}

/** Minimal proof-led hero for consultancies, services, B2B pages, and high-trust conversion flows. */
export function ProofLedSplitHero({
  eyebrow,
  title,
  description,
  primaryCta,
  secondaryCta,
  quote,
  stats = [],
  className,
}: ProofLedSplitHeroProps) {
  return (
    <section className={cn("relative overflow-hidden bg-background py-20 sm:py-24 lg:py-28", className)}>
      <div className="mx-auto grid w-full max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:px-8">
        <MotionDiv variants={fadeInLeft} className="max-w-3xl self-center">
          {eyebrow && <p className="mb-6 text-sm font-semibold uppercase tracking-[0.24em] text-primary">{eyebrow}</p>}
          <h1 className="font-heading text-5xl font-semibold leading-[0.92] tracking-tight text-foreground sm:text-6xl lg:text-7xl">
            {title}
          </h1>
          <p className="mt-7 max-w-2xl text-lg leading-8 text-muted-foreground sm:text-xl">
            {description}
          </p>
          {(primaryCta || secondaryCta) && (
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              {primaryCta && (
                <Button asChild size="xl" className="group rounded-button">
                  <Link href={primaryCta.href}>
                    {primaryCta.label}
                    <ArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </Button>
              )}
              {secondaryCta && (
                <Button asChild size="xl" variant="ghost" className="rounded-full">
                  <Link href={secondaryCta.href}>{secondaryCta.label}</Link>
                </Button>
              )}
            </div>
          )}
        </MotionDiv>

        <MotionDiv variants={blurIn} className="grid gap-4 self-center">
          {quote && (
            <Card className="rounded-panel border-border/70 bg-card p-7 shadow-[0_24px_80px_hsl(var(--foreground)/0.1)]">
              <blockquote className="font-heading text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl">
                &ldquo;{quote.body}&rdquo;
              </blockquote>
              <figcaption className="mt-8 border-t border-border/70 pt-5 text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">{quote.name}</span>
                {quote.role && <span> / {quote.role}</span>}
              </figcaption>
            </Card>
          )}

          {stats.length > 0 && (
            <MotionList className="grid gap-4 sm:grid-cols-3">
              {stats.map((stat) => (
                <MotionDiv key={stat.label} variants={scaleIn} className="rounded-card border border-border/70 bg-card p-5 shadow-sm">
                  <p className="font-heading text-4xl font-semibold tracking-tight text-foreground">{stat.value}</p>
                  <p className="mt-3 text-sm leading-6 text-muted-foreground">{stat.label}</p>
                </MotionDiv>
              ))}
            </MotionList>
          )}
        </MotionDiv>
      </div>
    </section>
  )
}